import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import JobCard from "@/components/JobCard";
import JobMap, { useGeocodedJobs } from "@/components/JobMap";
import CrewCompliance, { certStatus } from "@/components/CrewCompliance";
import JobsCalendar from "@/components/JobsCalendar";
import WeatherWidget from "@/components/WeatherWidget";
import { fmtTime } from "@/lib/helpers";
import { CalendarDays, Activity, Plus, List, Map as MapIcon, Maximize2, Flame, ChevronDown, ChevronRight, AlertTriangle, Radar, MonitorPlay, X, StickyNote } from "lucide-react";
import NotesPanel from "@/components/NotesPanel";

export default function Dashboard({ display }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [expandedPilots, setExpandedPilots] = useState(new Set());

  const load = useCallback(async () => {
    const [j, e, av] = await Promise.all([api.get("/jobs"), api.get("/employees"), api.get("/availability")]);
    setJobs(j.data); setEmployees(e.data); setAvailability(av.data);
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("jobs:refresh", h);
    return () => window.removeEventListener("jobs:refresh", h);
  }, [load]);

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));
  const now = new Date();
  const isUpcoming = (j) => new Date(j.start) >= new Date(now.toDateString()) && j.status !== "cancelled";
  const upcomingByEmployee = employees.map((e) => ({
    employee: e,
    jobs: jobs.filter((j) => isUpcoming(j) && j.employee_id === e.id).sort((a, b) => new Date(a.start) - new Date(b.start)),
  }));
  const unassigned = jobs.filter((j) => isUpcoming(j) && !empMap[j.employee_id]).sort((a, b) => new Date(a.start) - new Date(b.start));
  const totalUpcoming = jobs.filter(isUpcoming).length;

  const openNew = () => navigate("/jobs/new");
  const openJob = (job) => navigate(`/jobs/${job.id}`);

  const bneDay = (iso) => { try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" }); } catch { return ""; } };
  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" });
  const todaysJobs = jobs.filter((j) => bneDay(j.start) === todayStr);
  const todayPoints = useGeocodedJobs(
    todaysJobs.filter((j) => j.location).map((j) => ({
      id: j.id, title: j.title, location: j.location, time: fmtTime(j.start),
      pic: empMap[j.employee_id]?.name,
      assistants: (j.assistant_ids || []).map((id) => empMap[id]?.name).filter(Boolean),
    }))
  );

  return (
    <div className={display ? "space-y-8 fade-up p-6 min-h-screen bg-slate-100" : "space-y-8 fade-up"} data-testid={display ? "display-screen" : "dashboard"}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        {display ? (
          <div className="flex items-center gap-2">
            <Radar className="h-8 w-8 text-lime-500" />
            <span className="text-2xl font-bold font-heading text-slate-900">Air Log — Operations Board</span>
          </div>
        ) : (
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Control Room</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">
              Welcome, {user?.name?.split(" ")[0] || "Pilot"}
            </h1>
            <p className="text-slate-500 mt-1">Here's what your crew has on today.</p>
          </div>
        )}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {!display && (
            <a href="/display" target="_blank" rel="noopener noreferrer" data-testid="display-screen-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm bg-slate-900 hover:bg-slate-800 text-white transition-all">
              <MonitorPlay className="h-4 w-4" /> Display Screen
            </a>
          )}
          <a href="/map/heatmap/fullscreen" target="_blank" rel="noopener noreferrer" data-testid="heatmap-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm bg-orange-500 hover:bg-orange-400 text-white transition-all">
            <Flame className="h-4 w-4" /> Heat Map
          </a>
          <Button data-testid="new-job-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg">
            <Plus className="h-4 w-4 mr-1.5" /> New Job
          </Button>
          {display && (
            <a href="/dashboard" data-testid="display-exit-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm bg-slate-200 hover:bg-slate-300 text-slate-700 transition-all">
              <X className="h-4 w-4" /> Exit
            </a>
          )}
        </div>
      </div>

      {(() => {
        const alerts = [];
        employees.forEach((e) => (e.certifications || []).forEach((c) => {
          const st = certStatus(c.expiry);
          if (st.key === "expired" || st.key === "soon") alerts.push({ name: e.name, cert: c.name, st });
        }));
        if (alerts.length === 0) return null;
        const hasExpired = alerts.some((a) => a.st.key === "expired");
        return (
          <div data-testid="licence-alert-banner" className={`rounded-xl border p-4 ${hasExpired ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
            <div className={`flex items-center gap-2 font-heading font-semibold ${hasExpired ? "text-red-700" : "text-amber-700"}`}>
              <AlertTriangle className="h-4 w-4" /> Licence &amp; medical renewals need attention
            </div>
            <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
              {alerts.map((a, i) => (
                <li key={`${a.name}-${a.cert}`} className={a.st.key === "expired" ? "text-red-700" : "text-amber-700"} data-testid={`licence-alert-${i}`}>
                  <span className="font-medium">{a.name}</span> — {a.cert} <span className="opacity-80">({a.st.label})</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      <JobsCalendar jobs={jobs} todayKey={todayStr} count={2} stretch extra={<WeatherWidget />} empMap={empMap} dayPopover staffFilter availability={availability} />

      {/* Today's jobs — list / map */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-xl font-semibold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-lime-500" /> Today's Jobs <span className="text-slate-400">({todaysJobs.length})</span>
          </h2>
          <a href="/map/today/fullscreen" target="_blank" rel="noopener noreferrer" data-testid="map-fullscreen-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-lime-400 hover:bg-lime-300 text-slate-900">
            <Maximize2 className="h-4 w-4" /> Fullscreen Map
          </a>
        </div>
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="space-y-4" data-testid="today-jobs-list">
            {todaysJobs.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500">No jobs scheduled today.</div>
            ) : todaysJobs.map((job) => (
              <div key={job.id} data-testid={`today-job-${job.id}`}>
                <JobCard job={job} employee={empMap[job.employee_id]} empMap={empMap} showWeather onClick={() => openJob(job)} />
              </div>
            ))}
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100 h-[420px]" data-testid="today-map">
            <JobMap points={todayPoints} height="100%" />
          </div>
          <div data-testid="dashboard-notes">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><StickyNote className="h-4 w-4 text-lime-500" /> Notes</h3>
            <NotesPanel compact />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4 gap-3">
          <h2 className="text-xl font-semibold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <Activity className="h-5 w-5 text-lime-500" /> Upcoming Jobs by Pilot
          </h2>
          {totalUpcoming > 0 && (() => {
            const cols = [...upcomingByEmployee.map((x) => x.employee.id), ...(unassigned.length ? ["unassigned"] : [])];
            const allOpen = cols.length > 0 && cols.every((k) => expandedPilots.has(k));
            return (
              <Button variant="outline" size="sm" data-testid="expand-all-pilots-btn"
                onClick={() => setExpandedPilots(allOpen ? new Set() : new Set(cols))}>
                {allOpen ? "Collapse all" : "Expand all"}
              </Button>
            );
          })()}
        </div>
        {totalUpcoming === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
            <CalendarDays className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">No upcoming jobs. Add one or use voice entry.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="upcoming-by-pilot">
            {[...upcomingByEmployee.map((x) => ({ key: x.employee.id, employee: x.employee, jobs: x.jobs })),
              ...(unassigned.length ? [{ key: "unassigned", employee: null, jobs: unassigned }] : [])]
              .map(({ key, employee, jobs: ejobs }) => {
                const open = expandedPilots.has(key);
                const toggle = () => setExpandedPilots((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
                return (
                  <div key={key} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`pilot-column-${key}`}>
                    <button onClick={toggle} data-testid={`pilot-toggle-${key}`} className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
                      {open ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
                      <span className="h-8 w-8 rounded-full flex items-center justify-center font-semibold text-sm shrink-0" style={{ background: employee ? employee.color : "#e2e8f0", color: employee ? "#0f172a" : "#64748b" }}>
                        {employee ? employee.name.charAt(0) : "?"}
                      </span>
                      <span className="font-heading font-semibold text-slate-900 flex-1 min-w-0 truncate">{employee ? employee.name : "Unassigned"}</span>
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 shrink-0">{ejobs.length} upcoming</span>
                    </button>
                    {open && (
                      <div className="px-4 pb-4 pt-1 border-t border-slate-100 grid md:grid-cols-2 gap-3" data-testid={`pilot-jobs-${key}`}>
                        {ejobs.length === 0
                          ? <p className="text-sm text-slate-400 italic py-2">No upcoming jobs.</p>
                          : ejobs.map((job) => <JobCard key={job.id} job={job} employee={employee} empMap={empMap} onClick={() => openJob(job)} showDate />)}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Team */}
      <CrewCompliance employees={employees} jobs={jobs} onChanged={load} />
    </div>
  );
}
